// src/routes/Admin/Developer.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import { motion } from 'framer-motion';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import {
  ExpandMore as ExpandMoreIconTree,
  ChevronRight,
  Folder,
  Description,
} from '@mui/icons-material';

// 別ファイルにしたユーザー管理
import UserManagement from './UserManagement';
// import PromptManagement from './PromptManagement';
// import TreeManagement from './TreeManagement';
import ChatHistoryManagement from './ChatHistoryManagement';
import UserLogs from './UserLogs';
import FeedbackManagement from './FeedbackManagement';

// ---------------------
// structuredClone フォールバック
// ---------------------
function safeClone(obj) {
  if (typeof structuredClone === 'function') {
    return structuredClone(obj);
  }
  return JSON.parse(JSON.stringify(obj));
}

const initialTreeData = {
  id: 'root',
  label: 'インポートフォルダ',
  children: [
    { id: 'f1', label: '2023-09-01.csv' },
    {
      id: 'folder-reports',
      label: 'reports',
      children: [
        { id: 'file-q2', label: '2023Q2.pdf' },
        { id: 'file-q3', label: '2023Q3.pdf' },
      ],
    },
    { id: 'f2', label: 'some-other-file.csv' },
  ],
};

/** ツリー描画ヘルパー */
function renderTree(node) {
  const isFolder = Array.isArray(node.children) && node.children.length > 0;
  const icon = isFolder
    ? <Folder sx={{ mr: 0.5, fontSize: 18, color: '#7EB3FF' }} />
    : <Description sx={{ mr: 0.5, fontSize: 18 }} />;

  return (
    <TreeItem
      id={node.id}
      nodeId={node.id}
      key={node.id}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {icon}
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {node.label}
          </Typography>
        </Box>
      }
    >
      {isFolder && node.children.map(child => renderTree(child))}
    </TreeItem>
  );
}

/** フォルダ判定 */
function checkIsFolder(node, nodeId) {
  if (node.id === nodeId) return Array.isArray(node.children);
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const res = checkIsFolder(child, nodeId);
      if (res !== null) return res;
    }
  }
  return null;
}

/** サブフォルダ追加 (再帰) */
function createSubfolder(tree, folderId, newNode) {
  if (tree.id === folderId) {
    if (!Array.isArray(tree.children)) tree.children = [];
    tree.children.push(newNode);
    return;
  }
  if (Array.isArray(tree.children)) {
    for (const child of tree.children) {
      createSubfolder(child, folderId, newNode);
    }
  }
}

/** リネーム (再帰) */
function renameNode(tree, nodeId, newLabel) {
  if (tree.id === nodeId) {
    tree.label = newLabel;
    return;
  }
  if (Array.isArray(tree.children)) {
    for (const child of tree.children) {
      renameNode(child, nodeId, newLabel);
    }
  }
}

/** ノード削除 (再帰) */
function deleteNode(parent, nodeId) {
  if (!Array.isArray(parent.children)) return;
  for (let i = 0; i < parent.children.length; i++) {
    if (parent.children[i].id === nodeId) {
      parent.children.splice(i, 1);
      return;
    }
  }
  for (const child of parent.children) {
    deleteNode(child, nodeId);
  }
}

export default function Developer() {
  // タブ切り替え
  const [activeTab, setActiveTab] = useState(0);
  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  // ツリー管理
  const [treeData, setTreeData] = useState(initialTreeData);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [expanded, setExpanded] = useState(['root']);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleNodeSelect = (e, nodeId) => {
    setSelectedNodeId(nodeId);
  };
  const handleNodeToggle = (e, nodeIds) => {
    setExpanded(nodeIds);
  };

  // 追加・リネーム・削除
  const handleAddFolderToRoot = () => {
    const newName = prompt('メインフォルダ直下に作るフォルダ名:', 'main-subfolder');
    if (!newName) return;
    const updatedTree = safeClone(treeData);
    createSubfolder(updatedTree, 'root', { id: `folder-${Date.now()}`, label: newName, children: [] });
    setTreeData(updatedTree);
    setExpanded(prev => [...new Set([...prev, 'root'])]);
  };

  const handleAddFolder = () => {
    if (!selectedNodeId) {
      alert('追加先フォルダを選択してください');
      return;
    }
    if (!checkIsFolder(treeData, selectedNodeId)) {
      alert('選択先はフォルダではありません');
      return;
    }
    const newName = prompt('新規サブフォルダ名:', 'new-folder');
    if (!newName) return;
    const updatedTree = safeClone(treeData);
    createSubfolder(updatedTree, selectedNodeId, { id: `folder-${Date.now()}`, label: newName, children: [] });
    setTreeData(updatedTree);
    setExpanded(prev => [...new Set([...prev, selectedNodeId])]);
  };

  const handleRename = () => {
    if (!selectedNodeId) {
      alert('リネーム対象を選択してください');
      return;
    }
    const newName = prompt('新しい名前:');
    if (!newName) return;
    const updatedTree = safeClone(treeData);
    renameNode(updatedTree, selectedNodeId, newName);
    setTreeData(updatedTree);
  };

  const handleDelete = () => {
    if (!selectedNodeId) {
      alert('削除対象を選択してください');
      return;
    }
    if (selectedNodeId === 'root') {
      alert('rootフォルダは削除できません');
      return;
    }
    if (!window.confirm(`本当に削除しますか？ (ID=${selectedNodeId})`)) return;
    const updatedTree = safeClone(treeData);
    deleteNode(updatedTree, selectedNodeId);
    setTreeData(updatedTree);
    setSelectedNodeId(null);
  };

  // ファイルインポート (モック)
  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };
  const handleImport = () => {
    if (!selectedNodeId) {
      alert('インポート先フォルダを選んでください');
      return;
    }
    if (!checkIsFolder(treeData, selectedNodeId)) {
      alert('フォルダ以外にはインポートできません');
      return;
    }
    if (selectedFiles.length === 0) {
      alert('ファイルが選択されていません');
      return;
    }
    // ここでバックエンドAPI実装など
    alert(`(モック) インポート先: ${selectedNodeId} | ファイル数: ${selectedFiles.length}`);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
        管理者向けツール
      </Typography>

      {/* タブ切り替え */}
      <Tabs value={activeTab} onChange={handleChangeTab} textColor="primary" indicatorColor="primary">
        <Tab label="ユーザー管理" />
        {/* <Tab label="ツリー操作/インポート" /> */}
        {/* <Tab label="プロンプト管理" /> */}
        <Tab label="チャット履歴管理" />
        <Tab label="ユーザーログ" />
        <Tab label="フィードバック管理" />
      </Tabs>

      {/* ユーザー管理 */}
      {activeTab === 0 && (
        <Box sx={{ mt: 2 }}>
          <UserManagement />
        </Box>
      )}

      {/* ツリー操作・ファイルインポート */}
      {/* {activeTab === 1 && (
        <Box sx={{ mt: 2 }}>
           <TreeManagement />
        </Box>
      )} */}
      
      {/* プロンプト管理 */}
      {/* {activeTab === 1 && (
        <PromptManagement />
      )} */}

      {/* チャット履歴管理 */}
      {activeTab === 1 && (
        <Box sx={{ mt: 2 }}>
         <ChatHistoryManagement />
       </Box>
      )}

      {/* ユーザーログ */}
      {activeTab === 2 && (
        <Box sx={{ mt: 2 }}>
          <UserLogs />
        </Box>
      )}

      {/* フィードバック管理 */}
      {activeTab === 3 && (
        <Box sx={{ mt: 2 }}>
          <FeedbackManagement />
        </Box>
      )}
    </Box>
  );
}